import React from 'react';
import styled from 'styled-components';

import Btn from '../../buttons/Btn';
import { colors, radius, spacing } from '../tokens';
import { HandledBy, OrderInfo } from '../types';
import {
  Actions, BackLink, Panel, StepText, StepTitle,
} from './styles';

const CaseReference = styled.p`
  font-size: 24px;
  font-weight: 800;
  color: ${colors.brand};
  letter-spacing: 0.5px;
  margin: ${spacing.sm} 0 ${spacing.lg};
`;

const Timeline = styled.ol`
  list-style: none;
  counter-reset: timeline;
  padding: 0;
  margin: ${spacing.lg} 0;
`;

const TimelineItem = styled.li`
  counter-increment: timeline;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-size: 14px;
  color: ${colors.text};
  padding: ${spacing.xs} 0;

  &::before {
    content: counter(timeline);
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${colors.brandSoft};
    color: ${colors.brand};
    font-weight: 700;
    font-size: 12px;
  }
`;

const HandledByBlock = styled.div`
  padding: ${spacing.lg};
  border: 1px solid ${colors.border};
  border-radius: ${radius.medium};
  margin: ${spacing.lg} 0;
`;

const HandledByTitle = styled.div`
  font-weight: 700;
  color: ${colors.text};
  margin-bottom: ${spacing.xs};
`;

const EmailNotice = styled.p`
  font-size: 13px;
  color: ${colors.textMuted};
  margin: ${spacing.md} 0 0;
`;

type ConfirmationStepProps = {
  order: OrderInfo;
  demarcheName: string;
  serviceHref: string;
  hubHref: string;
  handledBy: HandledBy | null;
  caseId: string | null;
  isRefreshing: boolean;
  onRefresh: () => void;
};

// Step 6 (spec §5.11). The payment return proves nothing on its own (backend-plan.md §7.2) — the
// order's status is what says whether the webhook has actually run yet.
const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  order,
  demarcheName,
  serviceHref,
  hubHref,
  handledBy,
  caseId,
  isRefreshing,
  onRefresh,
}) => {
  if (order.status !== 'paid' && order.status !== 'completed') {
    return (
      <Panel>
        <StepTitle>Confirmation en cours</StepTitle>
        <StepText>
          Votre paiement a bien été reçu par Stripe et est en cours d’enregistrement. Cela prend
          généralement quelques secondes.
        </StepText>
        <Actions>
          <Btn
            type="button"
            name="demarche-funnel-confirmation-refresh"
            text="Rafraîchir"
            block
            isLoading={isRefreshing}
            disabled={isRefreshing}
            onClick={onRefresh}
          />
        </Actions>
      </Panel>
    );
  }

  const hasHandledBy = Boolean(handledBy?.company || handledBy?.person);

  return (
    <Panel>
      <StepTitle>Votre demande est enregistrée</StepTitle>
      <StepText>Voici la référence de votre dossier, à conserver pour tout échange avec notre équipe.</StepText>
      <CaseReference>{order.caseReference}</CaseReference>

      <Timeline>
        <TimelineItem>Commande enregistrée</TimelineItem>
        <TimelineItem>Paiement confirmé</TimelineItem>
        <TimelineItem>Vérification de votre dossier par notre équipe</TimelineItem>
        <TimelineItem>Transmission à l’administration par notre partenaire</TimelineItem>
      </Timeline>

      {hasHandledBy && (
        <HandledByBlock>
          <HandledByTitle>Qui s’occupe de votre dossier</HandledByTitle>
          <StepText>
            {[handledBy?.person, handledBy?.company].filter(Boolean).join(' — ')}
          </StepText>
        </HandledByBlock>
      )}

      <StepText>
        {`VroomVroom est un service privé indépendant de France Titres — ANTS. Notre équipe vous `
          + `recontactera par e-mail au sujet de votre démarche « ${demarcheName} ».`}
      </StepText>
      <EmailNotice>
        Un e-mail de confirmation vous a été envoyé — pensez à vérifier vos spams.
      </EmailNotice>

      {caseId && (
        <Actions>
          <BackLink href={`/student#/driving-code/demarches/${caseId}`}>Suivre mon dossier</BackLink>
        </Actions>
      )}
      <Actions>
        <BackLink href={serviceHref}>{`Revenir au guide « ${demarcheName} »`}</BackLink>
      </Actions>
      <Actions>
        <a href={hubHref}>Voir toutes les démarches</a>
      </Actions>
    </Panel>
  );
};

export { ConfirmationStep };
